import React from 'react';
import { debounce } from 'throttle-debounce';

import { useModalDispatch, useModalStore } from 'context/ModalContext';

import type { SetTimeoutReturnType } from 'types';

const QUEUE_CHECK_TIMER = 2000;
const DEBUG = false;

interface Options {
  debug?: boolean
}

export function useModalQueueObserver(options?: Options) {

  const {
    debug = DEBUG
  } = options || {};

  // Refs

  const timer = React.useRef<SetTimeoutReturnType | null>(null);

  // Hooks

  const { stack, queue } = useModalStore();
  const { moveModalFromQueueToStack } = useModalDispatch();

  // Handlers

  const log = React.useCallback((...params: any) => {
    if (debug) {
      console.log(...params);
    }
  }, [debug]);

  // Props

  const immediateQueue = queue.filter((item) => item.immediate);
  const delayedQueue = queue.filter((item) => !item.immediate);

  const stackSize = stack.size();
  const immediateQueueSize = immediateQueue.size();
  const delayedQueueSize = delayedQueue.size();

  // Handlers

  // --- Delayed Queue

  const checkDelayedQueue = React.useCallback(() => {
    log({ stack, queue });
    log('Resetting queue check interval...');

    if (timer.current) {
      clearTimeout(timer.current);
    }

    timer.current = setTimeout(() => {
      log('Checking queue...');
      if (stack.isEmpty() && delayedQueue.peek()) {
        log('Pushing modal from queue to stack');
        moveModalFromQueueToStack();
      } else {
        log('No modal to push from queue');
      }
    }, QUEUE_CHECK_TIMER);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    stack,
    delayedQueue,
    stackSize,
    delayedQueueSize
  ]);

  const debouncedCheckDelayedQueue = React.useCallback(
    debounce(500, checkDelayedQueue),
    [checkDelayedQueue]
  );

  // --- Immediate Queue

  const checkImmediateQueue = React.useCallback(() => {
    log('Checking immediate queue...');
    if (stack.isEmpty() && immediateQueue.peek()) {
      log('Pushing immediate modal from queue to stack');
      moveModalFromQueueToStack();
    } else {
      log('No modal to push from immediate queue');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    stack,
    immediateQueue,
    stackSize,
    immediateQueueSize
  ]);

  const debouncedCheckImmediateQueue = React.useCallback(
    debounce(500, checkImmediateQueue),
    [checkImmediateQueue]
  );

  // Effects

  // --- Delayed Queue

  React.useEffect(() => {
    debouncedCheckDelayedQueue();
    return () => {
      if (timer.current) {
        clearTimeout(timer.current);
      }
    };
  }, [debouncedCheckDelayedQueue]);

  // --- Immediate Queue

  React.useEffect(() => {
    debouncedCheckImmediateQueue();
  }, [debouncedCheckImmediateQueue]);

}
